import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { MapPin, Calendar, Package, ExternalLink, Camera, Image, Flower2, SprayCan, Wrench, CalendarClock } from 'lucide-react';
import { OrderStatusBadge } from './OrderStatusBadge';
import { DeliveryPhotoDialog } from './DeliveryPhotoDialog';
import { PartnerOrder } from '@/hooks/usePartnerOrders';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

interface OrderCardProps {
  order: PartnerOrder;
  onMarkDelivered: (file: File) => Promise<void>;
  onScheduleDelivery?: (date: string) => Promise<void>;
  isDelivering: boolean;
  readOnly?: boolean;
}

const getCategoryIcon = (category?: string) => {
  switch (category) {
    case 'flowers':
      return <Flower2 className="h-4 w-4 text-pink-500" />;
    case 'cleaning':
      return <SprayCan className="h-4 w-4 text-blue-500" />;
    case 'repairs':
      return <Wrench className="h-4 w-4 text-amber-500" />;
    default:
      return <Package className="h-4 w-4 text-muted-foreground" />;
  }
};

export const OrderCard = ({ order, onMarkDelivered, onScheduleDelivery, isDelivering, readOnly = false }: OrderCardProps) => {
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const hasWindow = !!order.delivery_window_start && !!order.delivery_window_end;
  const hasScheduledDate = hasWindow && order.delivery_date !== order.delivery_window_start;
  
  const googleMapsUrl = order.delivery_latitude && order.delivery_longitude
    ? `https://www.google.com/maps?q=${order.delivery_latitude},${order.delivery_longitude}`
    : null;

  const initials = order.memorial_name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleMarkDelivered = async (file: File) => {
    await onMarkDelivered(file);
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={order.memorial_profile_picture_url || undefined} alt={order.memorial_name} />
                <AvatarFallback className="text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-lg">{order.memorial_name}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Order #{order.id.slice(0, 8)} · {formatDistanceToNow(parseISO(order.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
            <OrderStatusBadge status={order.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Delivery Info */}
          {hasWindow ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>
                  Window: {format(parseISO(order.delivery_window_start!), 'MMM d')} – {format(parseISO(order.delivery_window_end!), 'MMM d, yyyy')}
                </span>
              </div>
              {hasScheduledDate && (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <CalendarClock className="h-4 w-4" />
                  <span>Scheduled: {format(parseISO(order.delivery_date), 'MMMM d, yyyy')}</span>
                </div>
              )}
              {!readOnly && (order.status === 'assigned' || order.status === 'accepted') && onScheduleDelivery && (
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-xs">
                      <CalendarClock className="h-3 w-3 mr-1" />
                      {hasScheduledDate ? 'Reschedule' : 'Schedule Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={parseISO(order.delivery_date)}
                      onSelect={async (date) => {
                        if (date) {
                          await onScheduleDelivery(format(date, 'yyyy-MM-dd'));
                          setDatePickerOpen(false);
                        }
                      }}
                      fromDate={parseISO(order.delivery_window_start!)}
                      toDate={parseISO(order.delivery_window_end!)}
                      defaultMonth={parseISO(order.delivery_window_start!)}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>
                Delivery: {format(parseISO(order.delivery_date), 'MMMM d, yyyy')}
              </span>
            </div>
          )}

          {/* Location & Headstone Photo */}
          <div className="space-y-1">
            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <MapPin className="h-4 w-4" />
                <span>View gravesite location</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {order.headstone_photo_url && (
              <a
                href={order.headstone_photo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Image className="h-4 w-4" />
                <span>View headstone photo</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {/* Order Items - full width */}
          <div className="space-y-2 pt-3 border-t">
            <p className="text-sm font-medium flex items-center gap-2">
              <Package className="h-4 w-4" />
              Order Items
            </p>
            <div className="space-y-1">
              {order.order_items.map(item => (
                <div key={item.id} className="flex justify-between text-sm items-center">
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(item.product_category)}
                    <span>{item.quantity}× {item.product_name}</span>
                  </div>
                  {item.fulfillment_cost_cents > 0 && (
                    <span className="text-muted-foreground">
                      {formatCurrency(item.fulfillment_cost_cents * item.quantity)}
                    </span>
                  )}
                </div>
              ))}
              <div className="flex justify-between text-sm font-medium pt-1 border-t">
                <span>{order.partner_payout_cents ? 'Your Earnings' : 'Est. Earnings'}</span>
                <span>{formatCurrency(order.partner_payout_cents || order.total_fulfillment_cost_cents || order.estimated_earnings_cents || 0)}</span>
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          {order.special_instructions && (
            <div className="space-y-1 pt-2 border-t">
              <p className="text-sm font-medium">Special Instructions</p>
              <p className="text-sm text-muted-foreground">{order.special_instructions}</p>
            </div>
          )}

          {/* Actions */}
          <div className="pt-3 border-t flex justify-end">
            {(order.status === 'assigned' || order.status === 'accepted') && !readOnly && (
              <Button onClick={() => setPhotoDialogOpen(true)} className="w-full lg:w-auto">
                <Camera className="h-4 w-4 mr-2" />
                Mark as Delivered
              </Button>
            )}
            {order.status === 'delivered' && order.delivery_photo_url && (
              <a 
                href={order.delivery_photo_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Camera className="h-3 w-3" />
                View delivery photo
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      <DeliveryPhotoDialog
        open={photoDialogOpen}
        onOpenChange={setPhotoDialogOpen}
        onConfirm={handleMarkDelivered}
        isLoading={isDelivering}
      />
    </>
  );
};
